<template>
	<div class="xs-12 flex xs-center wrap md-pt2 pb10">
		<aeg-case-proposal-list
			v-if="view === 'proposal-list'"
			@toggleAddProposalModal="toggleModal('addProposal')"
			@editProposal="editProposal"
		></aeg-case-proposal-list>
		<component v-else :is="view" @showProposalList="view = 'proposal-list'" />
		<core-modal name="addProposal" :headline="$t('add_offer')">
			<div class="p4">
				<div class="fw7 mb4">{{ addProposalButtonText }}</div>
				<div class="mb6">
					<div class="xs-12">
						<div class="block pb2">
							<label for="proposalName">Proposal name</label>
						</div>
						<j-base-input-text v-model="proposalName" id="proposalName"></j-base-input-text>
					</div>
				</div>
				<div
					v-if="selectProductInsuranceType && !offerHasMultipleLayers"
					class="mb6"
				>
					<div class="xs-12" style="min-height:300px;">
						<div class="block pb2">
							<label for="productType">{{ productTypeLabel }}</label>
						</div>
						<select-product
							v-model="productToAddToOffer"
							:insuranceType="selectProductInsuranceType"
						/>
					</div>
				</div>
				<j-button class="mt6" @click="handleAdd" :isLoading="isLoading">Add</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

import JButton from "@/components/JButton"
import SelectProduct from "./FormField/SelectProduct"
import JBaseInputText from "@/components/JBaseInputText"
import CoreModal from "@/modules/core/components/CoreModal"
import AegCaseProposalList from "./AegCaseProposalList"

import AegCaseLiabilityExhibition from "./MS/AegCaseLiabilityExhibition"
import AegCaseLiabilitySignboard from "./MS/AegCaseLiabilitySignboard"
import AegCaseJewellersTour from "./TO/AegCaseJewellersTour"
import AegCaseEmployeesCompensation from "./EC/AegCaseEmployeesCompensation"
import AegCaseCommercialBlock from "./CB/AegCaseCommercialBlock"
import AegCaseSendingInsurance from "./SH/AegCaseSendingInsurance"
import AegCaseJewellersBlock from "./JB/AegCaseJewellersBlock"
import AegCaseJewellersExhibition from "./EX/AegCaseJewellersExhibition"
import AegCasePersonalJewellery from "./PJ/AegCasePersonalJewellery"

import debounce from "lodash.debounce"

export default {
	components: {
		JButton,
		CoreModal,
		SelectProduct,
		JBaseInputText,
		AegCaseProposalList,
		AegCaseLiabilityExhibition,
		AegCaseLiabilitySignboard,
		AegCaseJewellersTour,
		AegCaseEmployeesCompensation,
		AegCaseCommercialBlock,
		AegCaseSendingInsurance,
		AegCaseJewellersBlock,
		AegCaseJewellersExhibition,
		AegCasePersonalJewellery
	},
	props: {
		addProposalButtonText: {
			type: String,
			default: "Add Proposal"
		},
		caseViews: {
			type: Object,
			required: true
		},
		productTypeLabel: {
			type: String,
			default: "Product Type"
		},
		selectProductInsuranceType: {
			type: String
		},
		offerHasMultipleLayers: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			view: "proposal-list",
			proposal: {},
			isActionLoading: null,
			isLoading: false,
			proposalName: "",
			productToAddToOffer: [],
			openModal: ""
		}
	},
	methods: {
		handleAdd: debounce(async function() {
			if (!this.offerHasMultipleLayers) this.addProposalAndOffer()
			else {
				await this.addProposal({ name: this.proposalName })
				this.toggleModal("addProposal")
				this.$store.dispatch("caseManagement/fetchProposals", {
					caseId: this.$store.state.caseManagement.caseId
				})
			}
		}, 600),

		editProposal(proposal) {
			// Set in store
			this.$store.commit("caseManagement/SET_PROPOSAL_ID", proposal.id)
			this.$store.commit("caseManagement/SET_OFFER_ID", proposal.offerId)

			// decide view
			if (!this.offerHasMultipleLayers) {
				console.log(this.productToAddToOffer)
				this.view = this.caseViews[proposal?.product?.productId?.plan]
			} else this.view = this.caseViews["default_view"]
		},
		toggleModal(typeId) {
			if (this.openModal === typeId) {
				this.openModal = ""
				this.$modal.hide(typeId)
			} else {
				this.openModal = typeId
				this.$modal.show(typeId)
			}
		},
		/**
		 * If no proposal and offer, create
		 */
		async init() {
			this.isLoading = true
			const proposal = this.$store.state.caseManagement?.proposals?.[0]
			const offer = this.$store.state.caseManagement?.proposals?.[0]
				?.basket?.[0]

			if (proposal && offer) {
				this.$store.commit("caseManagement/SET_PROPOSAL_ID", proposal?.id)
				this.$store.commit("caseManagement/SET_OFFER_ID", offer?.id)
				await this.$store.dispatch("caseManagement/fetchProposals", {
					caseId: this.$store.state.caseManagement.caseId,
					options: { currencyCodes: [] }
				})
			}

			this.isLoading = false
		},
		async addProposal(input = {}) {
			const query = gql`
				mutation addProposal($caseId: String!, $input: addProposalInput!) {
					addProposal(caseId: $caseId, input: $input) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			this.isLoading = true
			const res = await api({
				query,
				variables: {
					caseId: this.$store.state.caseManagement.caseId,
					input
				}
			})

			this.isLoading = false

			// Errors
			if (res?.data?.addProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addProposal?.errors?.[0] || this.$t("errors.general")
				)
				return Promise.resolve(res)
			}

			return Promise.resolve(res)
		},

		async addOffer() {
			const query = gql`
				mutation addOfferToProposal(
					$caseId: String!
					$proposalId: String!
					$input: addOfferInput!
				) {
					addOfferToProposal(
						caseId: $caseId
						proposalId: $proposalId
						input: $input
					) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const variables = {
				caseId: this.$store.state.caseManagement.caseId,
				proposalId: this.$store.state.caseManagement.proposalId,
				input: {
					productId: this.productToAddToOffer?.[0]?.productId
				}
			}

			const res = await api({
				query,
				variables
			})

			if (res?.data?.addOfferToProposal?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addOfferToProposal?.errors?.[0] ||
						this.$t("errors.general")
				)
				return Promise.resolve(res)
			}

			return Promise.resolve(res)
		},

		async addProposalAndOffer() {
			this.isLoading = true
			const res = await this.addProposal({ name: this.proposalName })
			this.$store.commit(
				"caseManagement/SET_PROPOSAL_ID",
				res?.data?.addProposal?.createdStatus?.id
			)
			const res2 = await this.addOffer()
			this.$store.commit(
				"caseManagement/SET_OFFER_ID",
				res2?.data?.addOfferToProposal?.createdStatus?.id
			)

			await this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId,
				options: { currencyCodes: [] }
			})
			this.isLoading = false
			this.toggleModal("addProposal")
		}
	},

	computed: {},

	mounted() {
		this.init()
	}
}
</script>

<style></style>
